import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { mainNavItems } from '../components/Layout/sidebarConfig';
import { hrNavItems } from '../components/Layout/sidebarHrConfig';
import { financeNavItems, reportsNavItems, vesselNavItems } from '../components/Layout/sidebarFinanceConfig';
import Dashboard from '../pages/Dashboard/Dashboard';
import Clients from '../pages/Clients/Clients';
import Layout from '../components/Layout/Layout';
import ProtectedRoute from '../components/Auth/ProtectedRoute';
import Login from '../pages/Auth/Login';
import Branches from '../pages/Branches';
import Providers from '../pages/Providers';
import Employees from '../pages/Employees';
import Purchases from '../pages/Purchases';
import Invoices from '../pages/Invoices';
import Bills from '../pages/Bills';
import Contracts from '../pages/Contracts';
import Categories from '../pages/Categories';
import Cars from '../pages/Cars';
import CrewAndCarsDocuments from '../pages/CrewAndCarsDocuments';
import DocumentSettings from '../pages/DocumentSettings';
import CrewChange from '../pages/CrewChange';
import CrewList from '../pages/CrewList';
import CrewListArabic from '../pages/CrewListArabic';
import ActiveCrewListArabic from '../pages/ActiveCrewListArabic';
import Roles from '../pages/Roles';
import SystemUsers from '../pages/SystemUsers';
import Ranks from '../pages/Ranks';
import Hiring from '../pages/Hiring';
import DeactivatedEmployees from '../pages/DeactivatedEmployees';

import Candidates from '../pages/Candidates';
import CandidatesEvaluation from '../pages/CandidatesEvaluation';
import Points from '../pages/Points';
import Locations from '../pages/Locations';
import CrewEvaluation from '../pages/CrewEvaluation';
import VesselReport from '../pages/VesselReport';
import CrewEmployeeHistory from '../pages/CrewEmployeeHistory';
import PersonnelCoastGuardPermits from '../pages/PersonnelCoastGuardPermits';
import CarCoastGuardPermits from '../pages/CarCoastGuardPermits';
import PersonnelLetters from '../pages/PersonnelLetters';
import CarLetters from '../pages/CarLetters';
import RaaftLetters from '../pages/RaaftLetters';
import TaeenLetters from '../pages/TaeenLetters';
import Banks from '../pages/Banks';
import Safes from '../pages/Safes';
import PettyCash from '../pages/PettyCash';
import Deductions from '../pages/Deductions';
import DebitNotes from '../pages/DebitNotes';
import FinanceReports from '../pages/FinanceReport';
import Currencies from '../pages/Currencies';
import Inventory from '../pages/Inventory';
import CodePrefixes from '../pages/CodePrefixes';
import FOAPage from '../pages/FOA/FOA';
import WebsiteSettings from '../pages/WebsiteSettings';
import Settings from '../pages/Settings';
import PaymentOrders from '../pages/PaymentOrders';
import Requisitions from '../pages/Requisitions/RequisitionsNew';
import DeliveryNotes from '../pages/DeliveryNotes';
import InvoiceHire from '../pages/InvoiceHire';
import { InTransactions, OutTransactions } from '../pages/Transactions';
import InvoicesReport from '../pages/Reports/InvoicesReport';
import BillsReport from '../pages/Reports/BillsReport';
import BanksReport from '../pages/Reports/BanksReport';
import SafesReport from '../pages/Reports/SafesReport';
import PaymentOrdersReport from '../pages/Reports/PaymentOrdersReport';
import RequisitionsReport from '../pages/Reports/RequisitionsReport';
import PurchasesReport from '../pages/Reports/PurchasesReport';
import SalaryReport from '../pages/Reports/SalaryReport';
import EmployeeDeductions from '../pages/EmployeeDeductions';
import Timesheet from '../pages/Timesheet';

// Placeholder components for other modules
const PlaceholderPage: React.FC<{ title: string }> = ({ title }) => (
  <div className="space-y-6">
    <div>
      <h1 className="text-2xl font-bold text-gray-900">{title}</h1>
      <p className="text-gray-600">This page is coming soon...</p>
    </div>
    <div className="card p-8 text-center">
      <div className="text-6xl mb-4">🚧</div>
      <h2 className="text-xl font-semibold text-gray-900 mb-2">Under Construction</h2>
      <p className="text-gray-600">
        The {title} module is currently being developed. Please check back later.
      </p>
    </div>
  </div>
);

// Landing: Dashboard only when the role has the dashboard permission;
// otherwise land on the first module the role is allowed to see (same order
// the sidebar renders them in).
const HomeGate: React.FC = () => {
  const { hasPermission } = useAuth();
  if (hasPermission('dashboard')) return <Dashboard />;
  const firstAllowed = [
    ...mainNavItems,
    ...hrNavItems,
    ...financeNavItems,
    ...vesselNavItems,
    ...reportsNavItems,
  ].find((item) => item.path !== '/' && (!item.permission || hasPermission(item.permission)));
  if (firstAllowed) return <Navigate to={firstAllowed.path} replace />;
  return (
    <div className="card p-8 text-center">
      <h2 className="text-xl font-semibold text-gray-900 mb-2">No modules assigned</h2>
      <p className="text-gray-600">Your role has no module permissions yet. Please contact your administrator.</p>
    </div>
  );
};

const AppRoutes: React.FC = () => {
  return (
    <Routes>
      {/* Public Route - Login */}
      <Route path="/login" element={<Login />} />
      
      {/* Protected Routes - All wrapped in Layout */}
      <Route path="/" element={<ProtectedRoute><Layout><HomeGate /></Layout></ProtectedRoute>} />
      <Route path="/clients" element={<ProtectedRoute requiredPermission="clients"><Layout><Clients /></Layout></ProtectedRoute>} />
      <Route path="/branches" element={<ProtectedRoute requiredPermission="branches"><Layout><Branches /></Layout></ProtectedRoute>} />
      <Route path="/providers" element={<ProtectedRoute requiredPermission="providers"><Layout><Providers /></Layout></ProtectedRoute>} />
      <Route path="/employees" element={<ProtectedRoute requiredPermission="employees"><Layout><Employees /></Layout></ProtectedRoute>} />
      <Route path="/purchases" element={<ProtectedRoute requiredPermission="purchases"><Layout><Purchases /></Layout></ProtectedRoute>} />
      <Route path="/invoices" element={<ProtectedRoute requiredPermission="invoices"><Layout><Invoices /></Layout></ProtectedRoute>} />
      <Route path="/bills" element={<ProtectedRoute requiredPermission="bills"><Layout><Bills /></Layout></ProtectedRoute>} />
      <Route path="/contracts" element={<ProtectedRoute requiredPermission="contracts"><Layout><Contracts /></Layout></ProtectedRoute>} />
      <Route path="/banks" element={<ProtectedRoute requiredPermission="banks"><Layout><Banks /></Layout></ProtectedRoute>} />
      <Route path="/safes" element={<ProtectedRoute requiredPermission="safes"><Layout><Safes /></Layout></ProtectedRoute>} />
      <Route path="/currencies" element={<ProtectedRoute requiredPermission="currencies"><Layout><Currencies /></Layout></ProtectedRoute>} />
      <Route path="/petty-cash" element={<ProtectedRoute requiredPermission="petty-cash"><Layout><PettyCash /></Layout></ProtectedRoute>} />
      <Route path="/in-transactions" element={<ProtectedRoute requiredPermission="transactions"><Layout><InTransactions /></Layout></ProtectedRoute>} />
      <Route path="/out-transactions" element={<ProtectedRoute requiredPermission="transactions"><Layout><OutTransactions /></Layout></ProtectedRoute>} />
      <Route path="/deductions" element={<ProtectedRoute requiredPermission="deductions"><Layout><Deductions /></Layout></ProtectedRoute>} />
      <Route path="/employee-deductions" element={<ProtectedRoute requiredPermission="employee-deductions"><Layout><EmployeeDeductions /></Layout></ProtectedRoute>} />
      <Route path="/timesheet" element={<ProtectedRoute requiredPermission="timesheet"><Layout><Timesheet /></Layout></ProtectedRoute>} />
      <Route path="/debit-notes" element={<ProtectedRoute requiredPermission="debit-notes"><Layout><DebitNotes /></Layout></ProtectedRoute>} />
      <Route path="/finance-reports" element={<ProtectedRoute requiredPermission="finance-reports"><Layout><FinanceReports /></Layout></ProtectedRoute>} />
      <Route path="/foa" element={<ProtectedRoute requiredPermission="invoices"><Layout><FOAPage /></Layout></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute requiredPermission="settings"><Layout><Settings /></Layout></ProtectedRoute>} />
      <Route path="/roles" element={<ProtectedRoute requiredPermission="roles"><Layout><Roles /></Layout></ProtectedRoute>} />
      <Route path="/system-users" element={<ProtectedRoute requiredPermission="system-users"><Layout><SystemUsers /></Layout></ProtectedRoute>} />
      <Route path="/categories" element={<ProtectedRoute requiredPermission="categories"><Layout><Categories /></Layout></ProtectedRoute>} />
      <Route path="/code-prefixes" element={<ProtectedRoute requiredPermission="code-prefixes"><Layout><CodePrefixes /></Layout></ProtectedRoute>} />
      <Route path="/inventory" element={<ProtectedRoute requiredPermission="inventory"><Layout><Inventory /></Layout></ProtectedRoute>} />
      <Route path="/cars" element={<ProtectedRoute requiredPermission="cars"><Layout><Cars /></Layout></ProtectedRoute>} />
      <Route path="/crew-and-cars-documents" element={<ProtectedRoute requiredPermission="crew-certificates"><Layout><CrewAndCarsDocuments /></Layout></ProtectedRoute>} />
      <Route path="/document-settings" element={<ProtectedRoute requiredPermission="document-settings"><Layout><DocumentSettings /></Layout></ProtectedRoute>} />
      <Route path="/crew-change" element={<ProtectedRoute requiredPermission="crew-change"><Layout><CrewChange /></Layout></ProtectedRoute>} />
      <Route path="/crew-list" element={<ProtectedRoute requiredPermission="crew-list"><Layout><CrewList /></Layout></ProtectedRoute>} />
      <Route path="/crew-list-arabic" element={<ProtectedRoute requiredPermission="crew-list-arabic"><Layout><CrewListArabic /></Layout></ProtectedRoute>} />
      <Route path="/active-crew-list-arabic" element={<ProtectedRoute requiredPermission="active-crew-list-arabic"><Layout><ActiveCrewListArabic /></Layout></ProtectedRoute>} />
      <Route path="/ranks" element={<ProtectedRoute requiredPermission="ranks"><Layout><Ranks /></Layout></ProtectedRoute>} />
      <Route path="/hiring" element={<ProtectedRoute requiredPermission="employees"><Layout><Hiring /></Layout></ProtectedRoute>} />
      <Route path="/deactivated-employees" element={<ProtectedRoute requiredPermission="deactivated-employees"><Layout><DeactivatedEmployees /></Layout></ProtectedRoute>} />
      <Route path="/candidates" element={<ProtectedRoute requiredPermission="candidates"><Layout><Candidates /></Layout></ProtectedRoute>} />
      <Route path="/candidates-evaluation" element={<ProtectedRoute requiredPermission="candidates"><Layout><CandidatesEvaluation /></Layout></ProtectedRoute>} />
      <Route path="/points" element={<ProtectedRoute requiredPermission="points"><Layout><Points /></Layout></ProtectedRoute>} />
      <Route path="/locations" element={<ProtectedRoute requiredPermission="locations"><Layout><Locations /></Layout></ProtectedRoute>} />
      <Route path="/crew-evaluation" element={<ProtectedRoute requiredPermission="crew-evaluation"><Layout><CrewEvaluation /></Layout></ProtectedRoute>} />
      <Route path="/vessel-report" element={<ProtectedRoute requiredPermission="vessel-report"><Layout><VesselReport /></Layout></ProtectedRoute>} />
      <Route path="/crew-employee-history" element={<ProtectedRoute requiredPermission="crew-employee-history"><Layout><CrewEmployeeHistory /></Layout></ProtectedRoute>} />
      <Route path="/personnel-coast-guard-permits" element={<ProtectedRoute requiredPermission="personnel-coast-guard-permits"><Layout><PersonnelCoastGuardPermits /></Layout></ProtectedRoute>} />
      <Route path="/car-coast-guard-permits" element={<ProtectedRoute requiredPermission="car-coast-guard-permits"><Layout><CarCoastGuardPermits /></Layout></ProtectedRoute>} />
      <Route path="/personnel-letters" element={<ProtectedRoute requiredPermission="personnel-letters"><Layout><PersonnelLetters /></Layout></ProtectedRoute>} />
      <Route path="/car-letters" element={<ProtectedRoute requiredPermission="car-letters"><Layout><CarLetters /></Layout></ProtectedRoute>} />
      <Route path="/raaft-letters" element={<ProtectedRoute requiredPermission="raaft-letters"><Layout><RaaftLetters /></Layout></ProtectedRoute>} />
      <Route path="/taeen-letters" element={<ProtectedRoute requiredPermission="taeen-letters"><Layout><TaeenLetters /></Layout></ProtectedRoute>} />
      <Route path="/petty-cash-request" element={<ProtectedRoute requiredPermission="petty-cash-request"><Layout><PlaceholderPage title="Petty Cash Request" /></Layout></ProtectedRoute>} />
      <Route path="/inspection" element={<ProtectedRoute requiredPermission="inspection"><Layout><PlaceholderPage title="Inspection" /></Layout></ProtectedRoute>} />
      <Route path="/representative" element={<ProtectedRoute requiredPermission="representative"><Layout><PlaceholderPage title="Representative" /></Layout></ProtectedRoute>} />
      <Route path="/supervisors" element={<ProtectedRoute requiredPermission="supervisors"><Layout><PlaceholderPage title="Supervisors" /></Layout></ProtectedRoute>} />
      <Route path="/payment-order" element={<ProtectedRoute requiredPermission="payment-order"><Layout><PaymentOrders /></Layout></ProtectedRoute>} />
      <Route path="/requisitions" element={<ProtectedRoute requiredPermission="requisitions"><Layout><Requisitions /></Layout></ProtectedRoute>} />
      <Route path="/delivery-notes" element={<ProtectedRoute requiredPermission="delivery-notes"><Layout><DeliveryNotes /></Layout></ProtectedRoute>} />
      <Route path="/invoice-hire" element={<ProtectedRoute requiredPermission="invoice-hire"><Layout><InvoiceHire /></Layout></ProtectedRoute>} />
      <Route path="/website-settings" element={<ProtectedRoute requiredPermission="website-settings"><Layout><WebsiteSettings /></Layout></ProtectedRoute>} />
      <Route path="/reports/purchases" element={<ProtectedRoute requiredPermission="purchases"><Layout><PurchasesReport /></Layout></ProtectedRoute>} />
      <Route path="/reports/invoices" element={<ProtectedRoute requiredPermission="invoices"><Layout><InvoicesReport /></Layout></ProtectedRoute>} />
      <Route path="/reports/bills" element={<ProtectedRoute requiredPermission="bills"><Layout><BillsReport /></Layout></ProtectedRoute>} />
      <Route path="/reports/banks" element={<ProtectedRoute requiredPermission="banks"><Layout><BanksReport /></Layout></ProtectedRoute>} />
      <Route path="/reports/safes" element={<ProtectedRoute requiredPermission="safes"><Layout><SafesReport /></Layout></ProtectedRoute>} />
      <Route path="/reports/payment-orders" element={<ProtectedRoute requiredPermission="payment-order"><Layout><PaymentOrdersReport /></Layout></ProtectedRoute>} />
      <Route path="/reports/requisitions" element={<ProtectedRoute requiredPermission="requisitions"><Layout><RequisitionsReport /></Layout></ProtectedRoute>} />
      <Route path="/reports/salary" element={<ProtectedRoute requiredPermission="salary-report"><Layout><SalaryReport /></Layout></ProtectedRoute>} />
      
      {/* Redirect any unknown routes to root */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

export default AppRoutes; 